import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { BriefResponseDto, BriefService } from '@/app/services/brief/brief.service';
import { ImageService } from '@/app/services/image/image.service';
import {
  briefInsightsNavItems,
  BriefInsightsNavItem,
  BriefInsightsSection,
} from '../../insights-nav';

@Component({
  selector: 'ins-brief',
  imports: [RouterOutlet],
  templateUrl: './brief.html',
  styleUrl: './brief.css',
  host: { class: 'flex flex-col h-full min-h-0 flex-1 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Brief implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private briefService = inject(BriefService);
  readonly imageService = inject(ImageService);

  readonly briefId = signal<string | undefined>(undefined);
  readonly brief = signal<BriefResponseDto | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  readonly navItems = briefInsightsNavItems;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.split('?')[0] ?? ''),
      startWith(this.router.url.split('?')[0] ?? ''),
    ),
    { initialValue: this.router.url.split('?')[0] ?? '' },
  );

  readonly activeSection = computed((): BriefInsightsSection | null => {
    const url = this.currentUrl();
    const match = url.match(/\/insights\/brief\/[^/]+\/([^/?]+)/);
    return (match?.[1] as BriefInsightsSection) ?? null;
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params.get('briefId') ?? undefined;
      this.briefId.set(id);
      if (id) {
        this.loadBrief(id);
      }
    });
  }

  goToSection(item: BriefInsightsNavItem): void {
    const id = this.briefId();
    if (!id) {
      return;
    }
    void this.router.navigate(['/insights/brief', id, item.section]);
  }

  getNavClass(item: BriefInsightsNavItem): string {
    if (this.activeSection() === item.section) {
      return 'border-primary text-accent';
    }
    return 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300';
  }

  briefSubtitle(b: BriefResponseDto): string {
    const campaign = b.campaign?.campaignName;
    const brand = b.businessAccount?.companyName;
    return [campaign, brand].filter(Boolean).join(' · ') || '—';
  }

  private loadBrief(briefId: string): void {
    this.loadingState.set('loading');
    this.briefService
      .getBriefById(briefId)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((brief) => {
        if (brief) {
          this.brief.set(brief);
          this.loadingState.set('loaded');
        }
      });
  }
}
